import type { User } from '@vizible/api' // adjust the import path for your User type
import { Logger } from '@vizible/utils'
import Cookies from 'js-cookie'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const logger = new Logger('UserStore')

// Permission types from backend
export interface PermissionResult {
    allowed: boolean
    reason?: string
}

export type UserPermissions = Record<string, PermissionResult>

// Helper to transform dates from strings to Date objects
const transformUser = (user: User): User => ({
    ...user,
    createdAt: new Date(user.createdAt),
    updatedAt: new Date(user.updatedAt),
    deletedAt: user.deletedAt ? new Date(user.deletedAt) : null,
})

interface UserStateLocal {
    user: User | null
    authState: {
        isOptimisticAuthed: boolean
        isFullyAuthed: boolean
    }
    permissions: {
        current: UserPermissions | null // permissions for current workspace context
        workspaceId: string | null // which workspace these permissions are for
        global: UserPermissions | null // global permissions (workspace creation, etc.)
        lastFetched: number | null // timestamp for cache invalidation
    }
    setUser: (user: User) => void
    setAuthState: (state: { isOptimisticAuthed: boolean; isFullyAuthed: boolean }) => void
    setPermissions: (permissions: UserPermissions, workspaceId?: string) => void
    getPermission: (key: string) => PermissionResult | null
    invalidatePermissions: () => void
    isPermissionCacheValid: (workspaceId?: string) => boolean
    clearUser: () => void
}

const PERMISSION_CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export const useUserStore = create(
    persist<UserStateLocal>(
        (set, get) => ({
            user: null,
            authState: {
                isOptimisticAuthed: !!Cookies.get('__session'),
                isFullyAuthed: false,
            },
            permissions: {
                current: null,
                workspaceId: null,
                global: null,
                lastFetched: null,
            },
            setUser: user => {
                logger.debug('Setting user data', { userId: user.id })
                set({ user: transformUser(user) })
            },
            setAuthState: authState => {
                logger.debug('Setting auth state', authState)
                set({ authState })
            },
            setPermissions: (permissions, workspaceId) => {
                logger.debug('Setting permissions', { workspaceId, permissionCount: Object.keys(permissions).length })
                set(state => ({
                    permissions: {
                        current: permissions,
                        workspaceId: workspaceId || null,
                        global: workspaceId ? state.permissions.global : permissions, // if no workspaceId, these are global
                        lastFetched: Date.now(),
                    },
                }))
            },
            getPermission: (key: string) => {
                const { permissions } = get()
                const permission = permissions.current?.[key]
                if (permission) {
                    logger.debug('Permission check', { key, allowed: permission.allowed, reason: permission.reason })
                    return permission
                }
                logger.debug('Permission not found', { key })
                return null
            },
            invalidatePermissions: () => {
                logger.debug('Invalidating permission cache')
                set(state => ({
                    permissions: {
                        ...state.permissions,
                        current: null,
                        workspaceId: null,
                        lastFetched: null,
                    },
                }))
            },
            isPermissionCacheValid: workspaceId => {
                const { permissions } = get()
                const now = Date.now()
                const isTimeValid = permissions.lastFetched ? now - permissions.lastFetched < PERMISSION_CACHE_TTL : false
                const isWorkspaceValid = permissions.workspaceId === (workspaceId || null)
                const hasPermissions = permissions.current !== null

                const isValid = isTimeValid && isWorkspaceValid && hasPermissions
                logger.debug('Permission cache validity check', {
                    workspaceId,
                    isTimeValid,
                    isWorkspaceValid,
                    hasPermissions,
                    isValid,
                })
                return isValid
            },
            clearUser: () => {
                logger.debug('Clearing user data')
                set({
                    user: null,
                    authState: {
                        isOptimisticAuthed: false,
                        isFullyAuthed: false,
                    },
                    permissions: {
                        current: null,
                        workspaceId: null,
                        global: null,
                        lastFetched: null,
                    },
                })
            },
        }),
        {
            name: 'user-storage',
            //storage: createJSONStorage(() => localStorage),
        }
    )
)
